import { 
    syncQueueDb, 
    getPendingSyncItems, 
    updateSyncItemStatus, 
    clearCompletedSyncItems,
    getPendingSyncCount,
    SyncQueueItem 
} from './syncQueue';
import { offlineStoreDb } from './offlineStoreDb';
import { useAuthStore } from '../store/authUserStore';

type SyncStatusCallback = (status: SyncStatus) => void;

export interface SyncStatus {
    isSyncing: boolean;
    pendingCount: number;
    lastSyncAt: string | null;
    error: string | null;
}

class SyncService {
    private static instance: SyncService;
    private isSyncing: boolean = false;
    private syncInterval: ReturnType<typeof setInterval> | null = null;
    private statusCallbacks: Set<SyncStatusCallback> = new Set();
    private lastSyncAt: string | null = null;
    private currentError: string | null = null;

    private constructor() {
        this.initSyncInterval();
        
        window.addEventListener('online', () => {
            console.log('[SyncService] Back online, starting sync...');
            this.syncNow();
        });
    }

    public static getInstance(): SyncService {
        if (!SyncService.instance) {
            SyncService.instance = new SyncService();
        }
        return SyncService.instance;
    }

    public subscribe(callback: SyncStatusCallback): () => void {
        this.statusCallbacks.add(callback);
        this.notifyStatusChange();
        return () => this.statusCallbacks.delete(callback);
    }

    private async notifyStatusChange(): Promise<void> {
        const pendingCount = await getPendingSyncCount();
        const status: SyncStatus = {
            isSyncing: this.isSyncing,
            pendingCount,
            lastSyncAt: this.lastSyncAt,
            error: this.currentError
        };
        this.statusCallbacks.forEach(cb => cb(status));
    }

    private initSyncInterval(): void {
        if (this.syncInterval) {
            clearInterval(this.syncInterval);
        }
        
        this.syncInterval = setInterval(() => {
            if (navigator.onLine && !this.isSyncing) {
                this.syncNow();
            }
        }, 5000);
    }

    public async syncNow(): Promise<void> {
        if (this.isSyncing || !navigator.onLine) {
            return;
        }

        this.isSyncing = true;
        this.currentError = null;
        await this.notifyStatusChange();

        try {
            const pendingItems = await getPendingSyncItems();
            
            if (pendingItems.length === 0) {
                console.log('[SyncService] No pending items to sync');
                this.lastSyncAt = new Date().toISOString();
                this.isSyncing = false;
                await this.notifyStatusChange();
                return;
            }

            console.log(`[SyncService] Syncing ${pendingItems.length} pending items...`);
            
            const token = useAuthStore.getState().token;
            if (!token) {
                console.warn('[SyncService] No auth token, skipping sync');
                this.isSyncing = false;
                await this.notifyStatusChange();
                return;
            }

            const headers = {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            };

            const sortedItems = pendingItems.sort((a, b) => 
                new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
            );

            for (const item of sortedItems) {
                await this.processSyncItem(item, headers);
            }

            await clearCompletedSyncItems();
            
            this.lastSyncAt = new Date().toISOString();
            console.log('[SyncService] Sync completed');

        } catch (error) {
            console.error('[SyncService] Sync error:', error);
            this.currentError = error instanceof Error ? error.message : 'Sync failed';
        } finally {
            this.isSyncing = false;
            await this.notifyStatusChange();
        }
    }

    private async processSyncItem(item: SyncQueueItem, headers: Record<string, string>): Promise<void> {
        if (!item.id) return;

        try {
            await updateSyncItemStatus(item.id, 'syncing');

            const response = await fetch(item.url, {
                method: item.method,
                headers,
                body: item.payload
            });

            if (response.ok) {
                console.log(`[SyncService] Successfully synced ${item.method} ${item.entityType}/${item.entityId}`);
                await updateSyncItemStatus(item.id, 'completed');
                
                if (item.method === 'DELETE') {
                    await this.cleanupLocalData(item.entityType, item.entityId);
                }
            } else if (response.status === 401) {
                console.warn('[SyncService] Auth token expired, stopping sync');
                await updateSyncItemStatus(item.id, 'pending');
                throw new Error('Authentication expired');
            } else if (response.status === 404 && item.method === 'DELETE') {
                console.log(`[SyncService] Item already deleted on server: ${item.entityId}`);
                await updateSyncItemStatus(item.id, 'completed');
            } else if (response.status === 409) {
                console.warn(`[SyncService] Conflict for ${item.entityType}/${item.entityId}`);
                await updateSyncItemStatus(item.id, 'failed', 'Conflict with server data');
            } else {
                const errorText = await response.text();
                console.error(`[SyncService] Failed to sync: ${response.status} ${errorText}`);
                await updateSyncItemStatus(item.id, 'failed', `HTTP ${response.status}`);
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            console.error(`[SyncService] Error syncing item:`, error);
            
            if (item.id) {
                await updateSyncItemStatus(item.id, 'failed', errorMessage);
            }
            
            if (errorMessage === 'Authentication expired') {
                throw error;
            }
        }
    }

    private async cleanupLocalData(entityType: string, entityId: string): Promise<void> {
        try {
            const tableMap: Record<string, any> = {
                'stands': offlineStoreDb.stands,
                'orders': offlineStoreDb.orders,
                'order-items': offlineStoreDb.orderItems,
                'stand-sectors': offlineStoreDb.standSectors,
                'products': offlineStoreDb.products,
                'customers': offlineStoreDb.customers,
                'events': offlineStoreDb.events
            };

            const table = tableMap[entityType];
            if (table) {
                await table.delete(entityId);
            }
        } catch (error) {
            console.error('[SyncService] Error cleaning up local data:', error);
        }
    }

    public async getStatus(): Promise<SyncStatus> {
        const pendingCount = await getPendingSyncCount();
        return {
            isSyncing: this.isSyncing,
            pendingCount,
            lastSyncAt: this.lastSyncAt,
            error: this.currentError
        };
    }

    public async retryFailed(): Promise<void> {
        const failedItems = await syncQueueDb.syncQueue
            .where('status')
            .equals('failed')
            .toArray();

        for (const item of failedItems) {
            if (item.id) {
                await updateSyncItemStatus(item.id, 'pending');
            }
        }

        await this.syncNow();
    }
}

export const syncService = SyncService.getInstance();

export default syncService;
